import { EventBus, Events } from "jiffy-store";
import { api } from "jiffy-api";
import { getOrderNumber } from "common/utils/user-context";
import SlidePanel from "components/jiffy/masthead-redesign2025/slide-panel/slide-panel";
import AddressEditor from "./address-editor";

/**
 * AddressEditorManager
 *
 * Main orchestrator for address edit functionality.
 * Handles:
 * - EventBus events for showing/hiding editor
 * - Managing edit vs create modes
 * - Coordinating between AddressEditor and SlidePanel
 * - Address list refresh after save
 *
 * @example
 * const manager = new AddressEditorManager();
 * manager.init();
 */
class AddressEditorManager {
  /** @type {SlidePanel|null} */
  static #slidePanel = null;

  /** @type {AddressEditor|null} */
  static #addressEditor = null;

  /** @type {Object|null} */
  static #currentAddress = null;

  static #PANEL_ID = "local-delivery-address-editor-slide-panel";

  /**
   * Gets the slide panel DOM element
   * @private
   * @static
   * @returns {HTMLElement|null}
   */
  static get #$slidePanelAddressList() {
    return document.querySelector(".js-slide-panel[data-panel-id='local-delivery-slide-panel']");
  }

  /**
   * Initializes the address editor manager
   * Binds event listeners for address editor events
   * @public
   * @static
   */
  static init() {
    AddressEditorManager.#bindEvents();
  }

  /**
   * Binds EventBus event listeners
   * @private
   * @static
   */
  static #bindEvents() {
    EventBus.on(Events.SHOW_ADDRESS_EDITOR_PANEL, AddressEditorManager.#handleShowEditor);
    EventBus.on(Events.ADDRESS_EDITOR_SUCCESS, AddressEditorManager.#handleEditorSuccess);
    EventBus.on(Events.ADDRESS_EDITOR_CANCEL, AddressEditorManager.#handleHideEditor);
  }

  /**
   * Shows the address editor panel
   * @param {Object} event - EventBus event object
   * @param {Object} [event.target] - Address data for edit mode (null for create mode)
   * @private
   * @static
   */
  static #handleShowEditor(event) {
    AddressEditorManager.#currentAddress = event.target || null;

    // Initialize slide panel if not already done
    if (!AddressEditorManager.#slidePanel) {
      AddressEditorManager.#initializeSlidePanel();
    }

    // Initialize address editor
    if (!AddressEditorManager.#addressEditor) {
      AddressEditorManager.#addressEditor = new AddressEditor();
    }

    // Initialize the editor with address data
    AddressEditorManager.#addressEditor.init(AddressEditorManager.#currentAddress);

    // Show the panel
    AddressEditorManager.#slidePanel.open();
  }

  /**
   * Hides the address editor panel
   * @private
   * @static
   */
  static #handleHideEditor() {
    AddressEditorManager.#slidePanel?.close();

    EventBus.one(Events.SLIDE_PANEL_CLOSED, () => {
      AddressEditorManager.#addressEditor?.destroy();
      AddressEditorManager.#addressEditor = null;
      AddressEditorManager.#currentAddress = null;
    });
  }

  /**
   * Handles successful address save/update
   * @private
   * @static
   */
  static async #handleEditorSuccess(event) {
    try {
      const addressId = event?.target?.id;
      const addressData = event?.target;
      await api.account.addresses.setOne(
        { order_number: getOrderNumber() },
        { ship_address_id: addressId }
      );

      if (AddressEditorManager.#$slidePanelAddressList?.dataset.pageReload === "true") {
        window.location.reload();
      } else {
        AddressEditorManager.#slidePanel?.close();
        EventBus.publish(Events.LOCAL_DELIVERY_ADDRESS_UPDATED, addressData);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error selecting address", error);
    }
  }

  /**
   * Initializes the slide panel
   * @private
   * @static
   */
  static #initializeSlidePanel() {
    AddressEditorManager.#slidePanel = new SlidePanel({
      panelId: AddressEditorManager.#PANEL_ID,
      closeOnBackdrop: false,
      closeOnEscape: false,
      onClose: AddressEditorManager.#handleHideEditor
    });
  }
}

export default AddressEditorManager;
